import { Container, Graphics, InteractionEvent, LINE_JOIN, Rectangle, Text } from "pixi.js"
import { DEFAULT_FONT_FAMILY, TEXT_ROLES } from "../text/typography"
import { BUILDING_SHAPES, BuildingShape } from "../../../../building_shapes"
import { SCALER } from "../../../../client_util"
import { chamfer, drawBoard, drawPlot, footprint, footprintStroke, groove, plotGrid,
    GOLD, GLYPH, INK, MUTED, RIM } from "./build_board"

// The shape picker that opens under the cursor while a builder holds Shift. See build_board.ts for
// what it is made of and why; everything here is this board's own layout.

export const PALETTE_W = 168
export const PALETTE_H = 100
// What the board occupies in STAGE units. The container divides its own y by SCALER, so every
// number below is in screen pixels -- but whatever positions the board from outside is not.
export const PALETTE_STAGE_H = PALETTE_H / SCALER

const CELL_W = 46
const CELL_H = 32
const CELL_GAP = 5
const CELL_Y = 26
const CELL_X = (PALETTE_W - (CELL_W * 3 + CELL_GAP * 2)) / 2
// Clear of the corner rivets, which is what sets the board's text margin.
const TEXT_X = 15

// Position the current tool under the pointer, accounting for this board's vertical scale.
export const buildingShapePaletteAnchor = (shape: BuildingShape) => ({
    x: CELL_X + BUILDING_SHAPES.indexOf(shape) * (CELL_W + CELL_GAP) + CELL_W / 2,
    y: (CELL_Y + CELL_H / 2) / SCALER,
})

// One plot: a square of dug ground under squared paper, with the footprint drawn on it.
//
// This owns its own plate rather than sitting on a UiButton, because the shared control is exactly
// the rounded slate rectangle this widget is getting away from, and the only thing left to borrow
// from it would be input -- which is three listeners.
class ShapeCell extends Graphics {
    private hovered = false
    private selected = false
    private filled = false
    private enabled = true

    constructor(private shape: BuildingShape, onSelect: () => void) {
        super()
        this.interactive = true
        this.cursor = "pointer"
        this.hitArea = new Rectangle(0, 0, CELL_W, CELL_H)
        this.on("pointerover", () => { if (this.enabled) { this.hovered = true; this.draw() } })
        this.on("pointerout", () => { this.hovered = false; this.draw() })
        // Press rather than release: the pointer is already down for the drag that follows.
        this.on("pointerdown", (event: InteractionEvent) => {
            event?.stopPropagation()
            if (this.enabled) onSelect()
        })
        this.draw()
    }

    setEnabled = (enabled: boolean) => {
        this.enabled = enabled
        this.interactive = enabled
        this.cursor = enabled ? "pointer" : "default"
        if (!enabled) this.hovered = false
        this.draw()
    }

    setSelected = (selected: boolean) => {
        this.selected = selected
        this.draw()
    }

    // Whether this shape prints solid. It is the material that decides (a wall is an outline,
    // everything else fills), not the shape -- so every cell wears the same answer, and a line is
    // neither either way.
    setFilled = (filled: boolean) => {
        this.filled = filled && this.shape !== "line"
        this.draw()
    }

    private draw = () => {
        const on = this.selected
        const ink = on ? GOLD : this.hovered ? INK : GLYPH
        const cx = CELL_W / 2
        const cy = CELL_H / 2
        this.clear()
        drawPlot(this, CELL_W, CELL_H, on, this.hovered)
        // The wash goes under the grid, not over it, so a filled footprint still reads as squares
        // being claimed rather than as a flat blob.
        if (this.filled) {
            this.lineStyle(0)
            this.beginFill(ink, 0.22)
            footprint(this, this.shape, cx, cy)
            this.endFill()
        }
        plotGrid(this, { x: 4, y: 4, w: CELL_W - 8, h: CELL_H - 8 }, cx, cy, on)
        footprintStroke(this, ink)
        footprint(this, this.shape, cx, cy)
        this.alpha = this.enabled ? 1 : 0.42
    }
}

export class BuildingShapePalette extends Container {
    private background = new Graphics()
    private chip = new Graphics()
    private buttons = new Map<BuildingShape, ShapeCell>()
    private caption = new Text("", { fontFamily: DEFAULT_FONT_FAMILY, fontSize: TEXT_ROLES.compactLabel.fontSize,
        fill: INK, letterSpacing: 1.2 })
    private mode = new Text("", { fontFamily: DEFAULT_FONT_FAMILY, fontSize: TEXT_ROLES.overline.fontSize,
        fill: MUTED, letterSpacing: 1 })
    private hint = new Text("Scroll or click · drag to draw", { fontFamily: DEFAULT_FONT_FAMILY,
        fontSize: TEXT_ROLES.compactCaption.fontSize, fill: MUTED })
    private correctionHint = new Text("Right-drag erase · Z undo", { fontFamily: DEFAULT_FONT_FAMILY,
        fontSize: TEXT_ROLES.compactCaption.fontSize, fill: MUTED })
    private signature = ""
    private locked = false

    constructor(onSelect: (shape: BuildingShape) => void) {
        super()
        this.visible = false
        this.zIndex = 60002
        this.scale.y = 1 / SCALER
        this.interactive = true
        this.hitArea = new Rectangle(0, 0, PALETTE_W, PALETTE_H)
        drawBoard(this.background, PALETTE_W, PALETTE_H)
        groove(this.background, TEXT_X - 4, PALETTE_W - TEXT_X + 4, 62.5)
        this.addChild(this.background)
        this.caption.position.set(TEXT_X, 10)
        this.hint.position.set(TEXT_X, 66)
        this.correctionHint.position.set(TEXT_X, 81)
        this.addChild(this.chip, this.caption, this.mode, this.hint, this.correctionHint)
        BUILDING_SHAPES.forEach((shape, index) => {
            const cell = new ShapeCell(shape, () => { if (!this.locked) onSelect(shape) })
            cell.position.set(CELL_X + index * (CELL_W + CELL_GAP), CELL_Y)
            this.buttons.set(shape, cell)
            this.addChild(cell)
        })
    }

    // The mode plate riveted to the board's top right: what the chosen shape will print as. While a
    // stroke is being dragged it fills gold and says so, which is also the signal that the cells
    // below have gone cold.
    private drawChip = (label: string, lit: boolean) => {
        this.mode.text = label
        const width = Math.max(28, Math.round(this.mode.width) + 12)
        const x = PALETTE_W - TEXT_X - width
        this.chip.clear()
        this.chip.lineStyle({ width: 1, color: lit ? GOLD : RIM, alpha: lit ? 1 : 0.55, join: LINE_JOIN.MITER })
        this.chip.beginFill(lit ? GOLD : 0x000000, lit ? 0.9 : 0.3)
        chamfer(this.chip, x, 9, width, 14, 3)
        this.chip.endFill()
        this.mode.style.fill = lit ? 0x2a1a0c : MUTED
        this.mode.position.set(x + (width - this.mode.width) / 2, 12)
    }

    setState = (visible: boolean, shape: BuildingShape, locked: boolean, hollow: boolean) => {
        this.visible = visible
        this.locked = locked
        const signature = `${shape}:${locked}:${hollow}`
        if (signature === this.signature) return
        this.signature = signature
        this.caption.text = shape.toUpperCase()
        this.drawChip(locked ? "DRAWING" : shape === "line" ? "STROKE" : hollow ? "OUTLINE" : "FILLED", locked)
        this.hint.text = locked ? "Release to start printing" : shape === "line" ? "Drag freely · Alt to snap" :
            shape === "rectangle" && !hollow ? "Drag area · Alt to align edges" : "Scroll or click · drag to draw"
        this.buttons.forEach((cell, candidate) => {
            cell.setEnabled(!locked)
            cell.setSelected(candidate === shape)
            cell.setFilled(!hollow)
        })
    }
}
